/**
 * Data protection hook for agent role restrictions.
 *
 * REQ D2-SEC-002: Disable print for agent role via CSS and JS.
 * REQ D2-SEC-003: Discourage screenshots (disable right-click, user-select).
 *
 * This hook applies protection measures when the user has the "agent" role:
 * - Blocks Ctrl+P / Cmd+P print shortcut with a notification (every agent-role user)
 * - Injects print media CSS to hide content (every agent-role user)
 * - Disables the right-click context menu (field agents only -- see below)
 * - Adds a CSS class that disables text selection on sensitive data (field
 *   agents only -- see below)
 *
 * #4548 (principal-approved): back-office desk staff (office admin, paralegal,
 * finance, finance/accounts, manager, accounts, HR, HR/paralegal) sit on core
 * role "agent" too, but they work the data all day and need to copy it. For a
 * user on one of COPY_ALLOWED_DESK_PRESETS the right-click and text-selection
 * blocks are lifted. The PRINT block is unchanged for them, and export rights
 * (canExport / canPrint) are unchanged for everyone. Fail closed: a plain field
 * agent (preset "none"), a canvasser, a custom bundle or any unknown preset
 * keeps every protection.
 */
import { useEffect } from "react";
import { useAuth } from "../contexts/auth";

/**
 * Back-office desk presets whose users may right-click and select/copy text
 * (#4548). Mirrors the backend ``PermissionPreset`` values in
 * ``apps.users.models`` -- the back-office desks, deliberately WITHOUT the
 * canvasser (a field role) and without ``none`` / ``custom``.
 */
export const COPY_ALLOWED_DESK_PRESETS = [
  "office_admin",
  "paralegal",
  "finance",
  "finance_accounts",
  "manager",
  "accounts",
  "hr",
  "hr_paralegal",
] as const;

/** True when the preset is a back-office desk allowed to copy (#4548). */
export function isCopyAllowedDeskPreset(preset: string | null | undefined): boolean {
  return (COPY_ALLOWED_DESK_PRESETS as readonly string[]).includes(preset ?? "");
}

export function useDataProtection() {
  const { user } = useAuth();
  const isAgent = user?.role === "agent";
  // #4548: copy/right-click protection applies to agent-role users who are NOT
  // on a back-office desk preset. Print protection still applies to every
  // agent-role user.
  const isCopyProtected = isAgent && !isCopyAllowedDeskPreset(user?.permission_preset);

  useEffect(() => {
    if (!isAgent) return;

    // D2-SEC-003: Disable right-click context menu on sensitive views
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Only block context menu on data areas, not inputs/textareas
      if (
        target.tagName !== "INPUT" &&
        target.tagName !== "TEXTAREA" &&
        !target.isContentEditable
      ) {
        e.preventDefault();
      }
    };

    // D2-SEC-002: Block Ctrl+P / Cmd+P print shortcut
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        e.preventDefault();
        // Show a brief notification
        showPrintBlockedNotification();
      }
    };

    // D2-SEC-002: Block window.print via beforeprint event
    const handleBeforePrint = (e: Event) => {
      e.preventDefault();
    };

    if (isCopyProtected) {
      document.addEventListener("contextmenu", handleContextMenu);
    }
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("beforeprint", handleBeforePrint);

    // D2-SEC-002: print protection marker for every agent-role user.
    document.body.classList.add("data-protected");
    // D2-SEC-003: user-select: none, field agents only (#4548).
    if (isCopyProtected) {
      document.body.classList.add("data-copy-protected");
    }

    // D2-SEC-002: Inject print media style to hide content for agents
    const printStyle = document.createElement("style");
    printStyle.id = "lwp-print-protection";
    printStyle.textContent = `
      @media print {
        body.data-protected * {
          display: none !important;
        }
        body.data-protected::after {
          display: block !important;
          content: "Printing is disabled for your role. Contact your administrator for data exports.";
          font-size: 24px;
          text-align: center;
          padding: 100px 20px;
          color: #007AFF;
          font-family: Inter, sans-serif;
        }
      }
    `;
    document.head.appendChild(printStyle);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("beforeprint", handleBeforePrint);
      document.body.classList.remove("data-protected");
      document.body.classList.remove("data-copy-protected");
      const styleEl = document.getElementById("lwp-print-protection");
      if (styleEl) styleEl.remove();
    };
  }, [isAgent, isCopyProtected]);

  return {
    isAgent,
    isCopyProtected,
    canExport: !isAgent && !!user,
    canPrint: !isAgent && !!user,
  };
}

function showPrintBlockedNotification() {
  // Create a temporary notification element
  const existing = document.getElementById("lwp-print-blocked");
  if (existing) existing.remove();

  const notification = document.createElement("div");
  notification.id = "lwp-print-blocked";
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #007AFF;
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 500;
    z-index: 99999;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    animation: fadeInOut 3s ease-in-out forwards;
  `;
  notification.textContent = "Printing is disabled for your role.";

  // Add fade animation
  const animStyle = document.createElement("style");
  animStyle.textContent = `
    @keyframes fadeInOut {
      0% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
      10% { opacity: 1; transform: translateX(-50%) translateY(0); }
      80% { opacity: 1; }
      100% { opacity: 0; }
    }
  `;
  document.head.appendChild(animStyle);
  document.body.appendChild(notification);

  setTimeout(() => {
    notification.remove();
    animStyle.remove();
  }, 3000);
}
